import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  Calculator, DollarSign, Percent, Calendar, Target, 
  AlertTriangle, TrendingUp, Shield, Info, ChevronRight
} from 'lucide-react';

// Configuration constants
const MIN_POSITION_VALUE = 250; // Minimum position value threshold

// Grid boundaries from JSON - numeric values for calculations
const TARGETS = [4, 5, 6, 8, 10];
const MAX_DDS = [2, 3, 4, 5, 6, 8, 10];
const RISKS = [0.25, 0.5, 0.75, 1.0];
const RISK_PRESETS = [0.25, 0.5, 0.6, 0.75, 0.85, 1.0]; // Extended preset buttons

// EXACT string keys as they appear in the JSON (critical for lookup)
const TARGET_KEYS = ['4', '5', '6', '8', '10'];
const MAXDD_KEYS = ['2', '3', '4', '5', '6', '8', '10'];
const RISK_KEYS = ['0.25', '0.5', '0.75', '1.0'];

// Preset account sizes
const ACCOUNT_SIZES = [25000, 50000, 100000, 150000];

// Risk presets per variation
const DEFAULT_RISK = { V1: 0.75, V2: 0.5 };

// Firm presets
const FIRM_PRESETS = [
  { label: 'Futures single-phase', target: 6, maxDD: 4, hint: '6% target / 4% DD' },
  { label: 'CFD 2-step — Step 1', target: 8, maxDD: 10, hint: '8% target / 10% DD' },
  { label: 'CFD 2-step — Step 2', target: 6, maxDD: 10, hint: '6% target / 10% DD' },
];

// Helper: clamp value to range
const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

// Helper: linear interpolation
const lerp = (a, b, t) => a + (b - a) * t;

// Helper: find bracketing indices and interpolation factor
const findBracket = (val, arr) => {
  const clamped = clamp(val, arr[0], arr[arr.length - 1]);
  for (let i = 0; i < arr.length - 1; i++) {
    if (clamped >= arr[i] && clamped <= arr[i + 1]) {
      const t = arr[i + 1] === arr[i] ? 0 : (clamped - arr[i]) / (arr[i + 1] - arr[i]);
      return { lo: i, hi: i + 1, t };
    }
  }
  return { lo: arr.length - 2, hi: arr.length - 1, t: 1 };
};

// Build the exact JSON key using string arrays
const buildKey = (targetIdx, maxDDIdx, riskIdx) => {
  return `${TARGET_KEYS[targetIdx]}|${MAXDD_KEYS[maxDDIdx]}|${RISK_KEYS[riskIdx]}`;
};

// Self-test: verify all 140 keys exist per variant (runs once on load)
const runSelfTest = (data) => {
  const variants = ['V1', 'V2'];
  let errors = 0;
  
  for (const variant of variants) {
    const varData = data[variant];
    if (!varData) {
      console.error(`[Calculator Self-Test] Missing variant: ${variant}`);
      errors++;
      continue;
    }
    
    for (let ti = 0; ti < TARGET_KEYS.length; ti++) {
      for (let di = 0; di < MAXDD_KEYS.length; di++) {
        for (let ri = 0; ri < RISK_KEYS.length; ri++) {
          const key = buildKey(ti, di, ri);
          if (!varData[key]) {
            console.error(`[Calculator Self-Test] Missing key: ${variant}["${key}"]`);
            errors++;
          }
        }
      }
    }
  }
  
  if (errors === 0) {
    console.log(`[Calculator Self-Test] PASSED: All ${TARGET_KEYS.length * MAXDD_KEYS.length * RISK_KEYS.length * 2} keys verified`);
  } else {
    console.error(`[Calculator Self-Test] FAILED: ${errors} missing keys`);
  }
  
  return errors === 0;
};

// Trilinear interpolation lookup
const lookupValue = (data, variant, target, maxDD, risk) => {
  const varData = data[variant];
  if (!varData) return null;

  const tBracket = findBracket(target, TARGETS);
  const dBracket = findBracket(maxDD, MAX_DDS);
  const rBracket = findBracket(risk, RISKS);

  // Get 8 corner values for trilinear interpolation
  const corners = [];
  let hasNull = false;

  for (const ti of [tBracket.lo, tBracket.hi]) {
    for (const di of [dBracket.lo, dBracket.hi]) {
      for (const ri of [rBracket.lo, rBracket.hi]) {
        const key = buildKey(ti, di, ri);
        const entry = varData[key];
        if (!entry) {
          console.warn(`[Calculator] Key not found: ${variant}["${key}"]`);
          corners.push({ pass: null, days: null });
          hasNull = true;
        } else {
          corners.push(entry);
          if (entry.days === null) hasNull = true;
        }
      }
    }
  }

  // Interpolate pass probability
  const interpolateField = (field) => {
    const vals = corners.map(c => c[field]);
    if (vals.some(v => v === null)) return null;
    
    // Trilinear: interpolate along risk axis first
    const r00 = lerp(vals[0], vals[1], rBracket.t);
    const r01 = lerp(vals[2], vals[3], rBracket.t);
    const r10 = lerp(vals[4], vals[5], rBracket.t);
    const r11 = lerp(vals[6], vals[7], rBracket.t);
    
    // Then along DD axis
    const d0 = lerp(r00, r01, dBracket.t);
    const d1 = lerp(r10, r11, dBracket.t);
    
    // Finally along target axis
    return lerp(d0, d1, tBracket.t);
  };

  const pass = interpolateField('pass');
  const days = interpolateField('days');

  // Check if outside grid bounds
  const isOutsideBounds = 
    target < TARGETS[0] || target > TARGETS[TARGETS.length - 1] ||
    maxDD < MAX_DDS[0] || maxDD > MAX_DDS[MAX_DDS.length - 1] ||
    risk < RISKS[0] || risk > RISKS[RISKS.length - 1];

  return { pass, days, hasNull, isOutsideBounds };
};

export function GetFundedCalculator() {
  const [lookupData, setLookupData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // User inputs
  const [accountSize, setAccountSize] = useState(50000);
  const [customAccount, setCustomAccount] = useState('');
  const [challengeFee, setChallengeFee] = useState(49);
  const [targetPct, setTargetPct] = useState(6);
  const [maxDDPct, setMaxDDPct] = useState(4);
  const [variation, setVariation] = useState('V1');
  const [riskPct, setRiskPct] = useState(0.75);

  // Load JSON data
  useEffect(() => {
    fetch('/monkalgo_calculator_lookup.json')
      .then(res => res.json())
      .then(data => {
        // Run self-test to verify all keys exist
        runSelfTest(data);
        setLookupData(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load calculator data:', err);
        setLoading(false);
      });
  }, []);

  // Update risk when variation changes
  useEffect(() => {
    setRiskPct(DEFAULT_RISK[variation]);
  }, [variation]);

  // Handle custom account input
  const handleCustomAccount = (val) => {
    setCustomAccount(val);
    const parsed = parseInt(val.replace(/[^0-9]/g, ''), 10);
    if (!isNaN(parsed) && parsed > 0) {
      setAccountSize(parsed);
    }
  };

  // Apply firm preset
  const applyPreset = (preset) => {
    setTargetPct(preset.target);
    setMaxDDPct(preset.maxDD);
  };

  // Calculate results
  const results = useMemo(() => {
    if (!lookupData) return null;

    const positionValue = accountSize * (riskPct / 100);
    const isBelowMinimum = positionValue < MIN_POSITION_VALUE;

    if (isBelowMinimum) {
      return { isBelowMinimum, positionValue };
    }

    const lookup = lookupValue(lookupData, variation, targetPct, maxDDPct, riskPct);
    if (!lookup) return null;

    const { pass, days, hasNull, isOutsideBounds } = lookup;
    
    const expectedAttempts = pass && pass > 0 ? 100 / pass : null;
    const expectedCost = expectedAttempts ? expectedAttempts * challengeFee : null;
    const costMultiplier = expectedAttempts ? expectedAttempts : null;

    return {
      isBelowMinimum: false,
      positionValue,
      pass,
      days,
      hasNull,
      isOutsideBounds,
      expectedAttempts,
      expectedCost,
      costMultiplier,
    };
  }, [lookupData, accountSize, targetPct, maxDDPct, variation, riskPct, challengeFee]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-[#7c3aed] border-t-transparent rounded-full mx-auto"></div>
        <p className="text-white/50 text-sm mt-4">Loading calculator...</p>
      </div>
    );
  }

  if (!lookupData) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-4" />
        <p className="text-white/50 text-sm">Unable to load calculator data</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mt-12"
      data-testid="get-funded-calculator"
    >
      {/* Section Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#7c3aed]/20 to-[#06b6d4]/20 border border-[#7c3aed]/30 text-[#7c3aed] text-xs font-mono mb-3">
          <Calculator size={14} />
          INTERACTIVE TOOL
        </div>
        <h3 className="font-heading font-bold text-2xl md:text-3xl text-white mb-2">
          Your Numbers — Get Funded Calculator
        </h3>
        <p className="text-white/50 text-sm max-w-2xl mx-auto">
          Enter your prop firm challenge parameters to see simulated pass probability and expected cost.
        </p>
      </div>

      <div className="glass rounded-2xl p-6 border border-white/10">
        {/* Firm Presets */}
        <div className="mb-6">
          <label className="block text-white/60 text-xs font-medium mb-2">Quick-fill firm preset</label>
          <div className="flex flex-wrap gap-2">
            {FIRM_PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() => applyPreset(preset)}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all border ${
                  targetPct === preset.target && maxDDPct === preset.maxDD
                    ? 'bg-[#7c3aed]/20 border-[#7c3aed]/50 text-[#7c3aed]'
                    : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`preset-${preset.target}-${preset.maxDD}`}
              >
                {preset.label}
                <span className="text-white/40 ml-1">({preset.hint})</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Left Column - Inputs */}
          <div className="space-y-5">
            {/* Account Size */}
            <div>
              <label className="block text-white/60 text-xs font-medium mb-2">
                <DollarSign size={12} className="inline mr-1" />
                Challenge Account Size
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {ACCOUNT_SIZES.map((size) => (
                  <button
                    key={size}
                    onClick={() => { setAccountSize(size); setCustomAccount(''); }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      accountSize === size && !customAccount
                        ? 'bg-[#06b6d4] text-white'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                    data-testid={`account-${size}`}
                  >
                    ${(size / 1000).toFixed(0)}K
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={customAccount}
                onChange={(e) => handleCustomAccount(e.target.value)}
                placeholder="Custom amount..."
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-[#7c3aed]/50"
                data-testid="account-custom"
              />
            </div>

            {/* Challenge Fee */}
            <div>
              <label className="block text-white/60 text-xs font-medium mb-2">
                <DollarSign size={12} className="inline mr-1" />
                Challenge Fee
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
                <input
                  type="number"
                  value={challengeFee}
                  onChange={(e) => setChallengeFee(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-7 pr-3 py-2 text-white text-sm focus:outline-none focus:border-[#7c3aed]/50"
                  data-testid="challenge-fee"
                />
              </div>
            </div>

            {/* Profit Target */}
            <div>
              <label className="block text-white/60 text-xs font-medium mb-2">
                <Target size={12} className="inline mr-1" />
                Profit Target %
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {TARGETS.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTargetPct(t)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      targetPct === t
                        ? 'bg-[#10B981] text-white'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                    data-testid={`target-${t}`}
                  >
                    {t}%
                  </button>
                ))}
              </div>
              <input
                type="number"
                value={targetPct}
                onChange={(e) => setTargetPct(Math.max(0, parseFloat(e.target.value) || 0))}
                step="0.5"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#7c3aed]/50"
                data-testid="target-input"
              />
            </div>

            {/* Max Drawdown */}
            <div>
              <label className="block text-white/60 text-xs font-medium mb-2">
                <Percent size={12} className="inline mr-1" />
                Max Drawdown %
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {MAX_DDS.map((d) => (
                  <button
                    key={d}
                    onClick={() => setMaxDDPct(d)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      maxDDPct === d
                        ? 'bg-[#F59E0B] text-white'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                    data-testid={`maxdd-${d}`}
                  >
                    {d}%
                  </button>
                ))}
              </div>
              <input
                type="number"
                value={maxDDPct}
                onChange={(e) => setMaxDDPct(Math.max(0, parseFloat(e.target.value) || 0))}
                step="0.5"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#7c3aed]/50"
                data-testid="maxdd-input"
              />
            </div>

            {/* Variation Toggle */}
            <div>
              <label className="block text-white/60 text-xs font-medium mb-2">Variation</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setVariation('V1')}
                  className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    variation === 'V1'
                      ? 'bg-[#10B981]/20 border border-[#10B981]/50 text-[#10B981]'
                      : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
                  }`}
                  data-testid="variation-v1"
                >
                  <Shield size={16} />
                  V1 · Conservative
                </button>
                <button
                  onClick={() => setVariation('V2')}
                  className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    variation === 'V2'
                      ? 'bg-[#7c3aed]/20 border border-[#7c3aed]/50 text-[#7c3aed]'
                      : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
                  }`}
                  data-testid="variation-v2"
                >
                  <TrendingUp size={16} />
                  V2 · Growth
                </button>
              </div>
            </div>

            {/* Risk Per Trade */}
            <div>
              <label className="block text-white/60 text-xs font-medium mb-2">Risk Per Trade</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {RISK_PRESETS.map((r) => (
                  <button
                    key={r}
                    onClick={() => setRiskPct(r)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      riskPct === r
                        ? 'bg-[#7c3aed] text-white'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                    data-testid={`risk-${r}`}
                  >
                    {r}%
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={riskPct}
                  onChange={(e) => setRiskPct(Math.max(0.1, Math.min(2, parseFloat(e.target.value) || 0.5)))}
                  step="0.05"
                  min="0.1"
                  max="2"
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#7c3aed]/50"
                  placeholder="Custom %"
                  data-testid="risk-input"
                />
                <span className="text-white/40 text-sm">%</span>
              </div>
              <p className="text-white/30 text-[10px] mt-1">Enter any value between 0.1% and 2%</p>
            </div>
          </div>

          {/* Right Column - Results */}
          <div>
            {results?.isBelowMinimum ? (
              /* Below Minimum Warning */
              <div className="h-full flex items-center justify-center">
                <div className="p-6 rounded-xl bg-yellow-500/10 border border-yellow-500/30 text-center">
                  <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-4" />
                  <h4 className="font-heading font-semibold text-yellow-500 text-lg mb-2">
                    Below Recommended Minimum
                  </h4>
                  <p className="text-white/60 text-sm leading-relaxed max-w-sm">
                    Position size: <span className="text-yellow-500 font-medium">${results.positionValue.toFixed(0)}</span>
                    <br /><br />
                    On wide-range days the 1-contract minimum position can exceed your intended risk. Consider a larger account or a lower-risk setup.
                  </p>
                </div>
              </div>
            ) : results ? (
              /* Results Card */
              <div className="h-full flex flex-col">
                {/* Outside Bounds Warning */}
                {results.isOutsideBounds && (
                  <div className="mb-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 flex items-start gap-2">
                    <Info size={14} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                    <span className="text-yellow-500/80 text-xs">
                      Estimated — outside tested range
                    </span>
                  </div>
                )}

                {/* Low Pass Probability Warning */}
                {results.pass !== null && results.pass < 85 && (
                  <div className="mb-4 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-start gap-2">
                    <AlertTriangle size={14} className="text-orange-500 flex-shrink-0 mt-0.5" />
                    <span className="text-orange-500/80 text-xs">
                      At this risk level, plan for multiple attempts — see expected cost.
                    </span>
                  </div>
                )}

                {/* Main Results Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {/* Pass Probability */}
                  <div className="p-4 rounded-xl bg-gradient-to-br from-[#10B981]/20 to-[#10B981]/5 border border-[#10B981]/30">
                    <div className="text-[#10B981]/60 text-xs font-medium mb-1">Simulated Pass Probability</div>
                    <div className="text-[#10B981] font-heading font-bold text-2xl">
                      {results.pass !== null ? `${results.pass.toFixed(1)}%` : 'N/A'}
                    </div>
                  </div>

                  {/* Days to Pass */}
                  <div className="p-4 rounded-xl bg-gradient-to-br from-[#06b6d4]/20 to-[#06b6d4]/5 border border-[#06b6d4]/30">
                    <div className="text-[#06b6d4]/60 text-xs font-medium mb-1">Avg. Days to Pass</div>
                    <div className="text-[#06b6d4] font-heading font-bold text-2xl">
                      {results.days !== null ? (
                        <>~{Math.round(results.days)} <span className="text-sm font-normal">calendar</span></>
                      ) : results.hasNull ? '200+' : 'N/A'}
                    </div>
                  </div>

                  {/* Risk Per Trade $ */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-white/40 text-xs font-medium mb-1">Risk Per Trade</div>
                    <div className="text-white font-heading font-bold text-2xl">
                      ${results.positionValue.toLocaleString()}
                    </div>
                  </div>

                  {/* Expected Attempts */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-white/40 text-xs font-medium mb-1">Expected Attempts</div>
                    <div className="text-white font-heading font-bold text-2xl">
                      {results.expectedAttempts !== null ? results.expectedAttempts.toFixed(2) : 'N/A'}
                    </div>
                  </div>
                </div>

                {/* Expected Cost */}
                <div className="p-4 rounded-xl bg-gradient-to-r from-[#7c3aed]/20 to-[#06b6d4]/20 border border-[#7c3aed]/30 mb-4">
                  <div className="text-[#7c3aed]/60 text-xs font-medium mb-1">Expected Cost to Get Funded</div>
                  <div className="text-white font-heading font-bold text-2xl">
                    {results.expectedCost !== null ? (
                      <>≈ ${results.expectedCost.toFixed(0).toLocaleString()}</>
                    ) : 'N/A'}
                  </div>
                </div>

                {/* Summary Sentence */}
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5 flex-1">
                  <p className="text-white/70 text-sm leading-relaxed">
                    On a <span className="text-white font-medium">${accountSize.toLocaleString()}</span> challenge 
                    (<span className="text-[#10B981]">{targetPct}%</span> target / <span className="text-[#F59E0B]">{maxDDPct}%</span> max DD), 
                    <span className="text-white font-medium"> {variation}</span> at <span className="text-[#7c3aed]">{riskPct}%</span> risk 
                    = <span className="text-white font-medium">${results.positionValue.toLocaleString()}</span> per trade 
                    → <span className="text-[#10B981] font-medium">{results.pass !== null ? `${results.pass.toFixed(1)}%` : 'N/A'}</span> simulated pass probability 
                    in <span className="text-[#06b6d4] font-medium">~{results.days !== null ? Math.round(results.days) : '200+'}</span> trading days.
                    {results.costMultiplier !== null && (
                      <> Expected cost to get funded ≈ <span className="text-[#7c3aed] font-medium">{results.costMultiplier.toFixed(2)}×</span> your challenge fee.</>
                    )}
                  </p>
                </div>

                {/* Footnote */}
                <div className="mt-4 p-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <p className="text-white/30 text-[10px] leading-relaxed">
                    <Info size={10} className="inline mr-1" />
                    Simulated probability from historical backtest data (50,000 runs at published presets; 8,000 per custom configuration). 
                    Not a guarantee. Backtested performance does not assure future results.
                  </p>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-white/40 text-sm">Enter parameters to see results</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
